import { React } from '@/lib/react';
import { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { supabase } from '../../supabaseClient';
import { PropertyProvider } from './PropertyContext';

interface UserData {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone_number?: string;
  brokerage: string;
  market_area: string;
  created_at: string;
  updated_at: string;
  headshot_url?: string;
  user_tones?: string;
}

interface UserContextType {
  userData: UserData | null;
  loading: boolean;
}

const UserContext = createContext<UserContextType>({
  userData: null,
  loading: true,
});

export const useUser = () => useContext(UserContext);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);

  // Memoize the fetch user data function
  const fetchUserData = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching user data:', error);
        setUserData(null);
      } else {
        setUserData(data as UserData);
      }
    } catch (error) {
      console.error('Error in user data fetch:', error);
      setUserData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Memoize the update user state function
  const updateUserState = useCallback((session: any) => {
    if (session?.user) {
      fetchUserData(session.user.id);
    } else {
      setUserData(null);
      setLoading(false);
    }
  }, [fetchUserData]);

  useEffect(() => {
    let mounted = true;

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (mounted) {
        updateUserState(session);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (mounted) {
        updateUserState(session);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [updateUserState]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    userData,
    loading
  }), [userData, loading]);

  return (
    <UserContext.Provider value={contextValue}>
      <PropertyProvider>
        {children}
      </PropertyProvider>
    </UserContext.Provider>
  );
}; 